/* ============================================================
   <cart-free-gift>
   ------------------------------------------------------------
   Self-contained free-gift card for the cart drawer. Does NOT touch the
   theme's encrypted cart JS.

   - Adds the chosen gift via /cart/add.js (line property `_free_gift`) and
     refreshes the drawer + cart count using Shopify's Section Rendering API.
   - 2-up carousel with prev/next arrows for the gift options.
   - Auto-removes the gift if the cart drops below the threshold.
   - Locks the gift line quantity and tags it with a "Free gift" badge.
   ============================================================ */
(function () {
  if (customElements.get('cart-free-gift')) return;

  var ADD_URL = (window.Shopify && Shopify.routes && Shopify.routes.cart_add_url) || '/cart/add';

  class CartFreeGift extends HTMLElement {
    connectedCallback() {
      if (this._init) return;
      this._init = true;

      // Always-on interactions for the current render.
      this.addEventListener('click', this.onClick.bind(this));
      this.setupCarousel();

      // ── 0) Cross-tier reconciliation — DISABLED ────────────────────
      // Stale-key auto-removal is intentionally turned off for now. The
      // snippet still annotates `data-stale-gift-keys` for future use, but
      // the JS does nothing with it. Cart behaviour is back to the previous
      // mode: lower-tier gifts persist when a higher tier is reached.
      // To re-enable, uncomment the block below.
      //
      // var stale = (this.dataset.staleGiftKeys || '')
      //   .split(',').map(function (k) { return k.trim(); }).filter(Boolean);
      // if (stale.length) {
      //   this.removeLine(stale[0]);
      //   return;
      // }

      var met = this.dataset.thresholdMet === 'true';
      var keys = (this.dataset.giftKeys || '')
        .split(',').map(function (k) { return k.trim(); }).filter(Boolean);

      // Below threshold but a gift is still in the cart -> remove it.
      if (!met && keys.length) { this.removeLine(keys[0]); return; }
      // More than one gift for the active tier -> drop the extra.
      if (keys.length > 1) { this.removeLine(keys[keys.length - 1]); return; }
      // Decorate the single gift line.
      if (keys.length === 1) { this.decorateGiftLine(keys[0]); }
    }

    /* -------------------- clicks -------------------- */
    onClick(e) {
      var choose = e.target.closest('.cart-free-gift-choose');
      if (choose && !choose.disabled) {
        e.preventDefault();
        var option = choose.closest('.cart-free-gift-option');
        var select = option && option.querySelector('.cart-free-gift-variant');
        var variantId = select ? select.value : choose.dataset.variantId;
        this.addGift(variantId, choose);
        return;
      }
      var change = e.target.closest('[data-cfg-change]');
      if (change) {
        e.preventDefault();
        var keys = (this.dataset.giftKeys || '').split(',').map(function (k) { return k.trim(); }).filter(Boolean);
        if (keys.length) { change.disabled = true; this.removeLine(keys[0]); }
      }
    }

    /* -------------------- add gift -------------------- */
    addGift(variantId, btn) {
      if (!variantId) return;
      btn.classList.add('loading');
      btn.disabled = true;
      var self = this;
      var tier = this.dataset.tier || '1';

      fetch(ADD_URL + '.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          items: [{
            id: Number(variantId),
            quantity: 1,
            properties: { _free_gift: 'true', _free_gift_tier: tier }
          }],
          sections: 'offcanvas-cart,cart-count-badge',
          sections_url: window.location.pathname
        })
      })
        .then(function (r) { return r.json(); })
        .then(function (data) {
          if (data && data.status && data.status >= 400) {
            self.showError(data.description || data.message);
            btn.classList.remove('loading'); btn.disabled = false;
            return;
          }
          self.applySections(data.sections || {});
        })
        .catch(function () {
          btn.classList.remove('loading'); btn.disabled = false;
        });
    }

    /* Swap drawer body + header cart badge with freshly rendered sections.
       Replace the <cart-container> host element (not innerHTML) so the theme's
       custom-element connectedCallback re-fires and qty/delete listeners rebind. */
    applySections(sections) {
      if (sections['offcanvas-cart']) {
        var doc = new DOMParser().parseFromString(sections['offcanvas-cart'], 'text/html');
        var freshContainer = doc.querySelector('cart-container');
        var currentContainer = document.querySelector('#offcanvas-cart cart-container');
        if (freshContainer && currentContainer) {
          currentContainer.replaceWith(freshContainer);
        } else {
          // Fallback: outer offcanvas swap
          var freshOff = doc.querySelector('#offcanvas-cart');
          var currentOff = document.querySelector('#offcanvas-cart');
          if (freshOff && currentOff) currentOff.innerHTML = freshOff.innerHTML;
        }
      }
      if (sections['cart-count-badge']) {
        document.querySelectorAll('.cart-count-badge').forEach(function (el) {
          el.outerHTML = sections['cart-count-badge'];
        });
      }
    }

    showError(msg) {
      var alert = document.querySelector('#cart-alert');
      if (alert) {
        var m = alert.querySelector('[data-alert-msg]');
        if (m) m.textContent = msg || 'Could not add the gift.';
        alert.hidden = false;
      }
    }

    /* -------------------- carousel -------------------- */
    setupCarousel() {
      var wrap = this.querySelector('[data-cfg-carousel]');
      if (!wrap) return;
      var track = wrap.querySelector('[data-cfg-track]');
      var prev = wrap.querySelector('[data-cfg-prev]');
      var next = wrap.querySelector('[data-cfg-next]');
      if (!track) return;

      var update = function () {
        var overflow = track.scrollWidth - track.clientWidth > 4;
        if (!overflow) {
          if (prev) prev.hidden = true;
          if (next) next.hidden = true;
          wrap.classList.remove('has-overflow');
          return;
        }
        wrap.classList.add('has-overflow');
        var atStart = track.scrollLeft <= 2;
        var atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
        if (prev) { prev.hidden = false; prev.disabled = atStart; }
        if (next) { next.hidden = false; next.disabled = atEnd; }
      };

      var page = function (dir) {
        track.scrollBy({ left: dir * Math.round(track.clientWidth * 0.9), behavior: 'smooth' });
      };

      if (prev) prev.addEventListener('click', function () { page(-1); });
      if (next) next.addEventListener('click', function () { page(1); });
      track.addEventListener('scroll', function () { window.requestAnimationFrame(update); }, { passive: true });
      window.requestAnimationFrame(update);
    }

    /* -------------------- remove / decorate -------------------- */
    removeLine(key) {
      if (!key) return;
      var sel = '.btn-line-item-remove[data-line-item-key="' + CSS.escape(key) + '"]';
      var click = function () {
        var btn = document.querySelector(sel);
        if (btn) { btn.click(); return; }
        var input = document.querySelector('input[data-line-item-key="' + CSS.escape(key) + '"]');
        if (input) { input.value = 0; input.dispatchEvent(new Event('change', { bubbles: true })); }
      };
      window.requestAnimationFrame(function () { setTimeout(click, 30); });
    }

    decorateGiftLine(key) {
      var input = document.querySelector('input[data-line-item-key="' + CSS.escape(key) + '"]');
      if (!input) return;
      var item = input.closest('.cart-item');
      if (!item || item.dataset.cfgDecorated) return;

      // Only label the line "FREE GIFT" when the line truly resolved to 0
      // (cart-items.liquid adds .cart-free-gift-line-free on the price span
      // when final_line_price === 0). Otherwise the discount hasn't applied
      // and the shopper is paying for it — don't lie about it.
      var isFree = !!item.querySelector('.cart-free-gift-line-free');
      if (!isFree) return;

      item.dataset.cfgDecorated = '1';
      item.classList.add('is-free-gift');

      input.setAttribute('readonly', 'readonly');
      input.setAttribute('min', '1');
      var stepper = item.querySelector('.qty-input-wrapper');
      if (stepper) {
        stepper.querySelectorAll('button').forEach(function (b) { b.setAttribute('disabled', 'disabled'); });
      }

      // Hide the (often negative) low-stock warning on the gift line.
      var stockInfo = item.querySelector('.cart-item-stock-info');
      if (stockInfo) stockInfo.style.display = 'none';

      var title = item.querySelector('.cart-item-product-title');
      if (title && !item.querySelector('.cart-free-gift-line-badge')) {
        var badge = document.createElement('span');
        badge.className = 'cart-free-gift-line-badge';
        badge.style.background = this.dataset.freeColor || '#1f8a4c';
        badge.innerHTML = '<i class="fa-solid fa-gift" aria-hidden="true"></i> ' + (this.dataset.lineBadge || 'FREE');
        title.parentNode.insertBefore(badge, title);
      }
    }
  }

  customElements.define('cart-free-gift', CartFreeGift);
})();
